import { AppliedCoupon, CouponKind } from '../types/payment';

const toUpperString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.toUpperCase() : null;
};

const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

export const normalizeCouponCode = (value: string): string =>
  value.replace(/\s+/g, '').toUpperCase().slice(0, 20);

export const parseCouponValidationResponse = (data: any, normalizedCode: string): AppliedCoupon => {
  const rawPercent =
    data?.discountPercent ??
    data?.coupon?.discountPercent ??
    data?.percent ??
    data?.coupon?.percent;

  const rawFreeDays =
    data?.freeDays ??
    data?.coupon?.freeDays ??
    data?.trialDays ??
    data?.coupon?.trialDays ??
    data?.days ??
    data?.coupon?.days ??
    data?.accessDays ??
    data?.coupon?.accessDays;

  const rawType =
    data?.type ??
    data?.coupon?.type ??
    data?.couponType ??
    data?.coupon?.couponType ??
    data?.kind ??
    data?.coupon?.kind;

  const typeFromApi = toUpperString(rawType);
  const freeDays = toPositiveNumber(rawFreeDays);
  const discountPercentRaw = toPositiveNumber(rawPercent);

  const isTypeFreeDays = typeFromApi === 'FREE_DAYS' || typeFromApi === 'TRIAL_DAYS';
  const isTypeDiscount = typeFromApi === 'DISCOUNT_PERCENT' || typeFromApi === 'PERCENT' || typeFromApi === 'DISCOUNT';

  if (freeDays) {
    return {
      code: normalizedCode,
      type: 'FREE_DAYS',
      freeDays: Math.min(365, Math.round(freeDays)),
      message: data?.message ?? data?.coupon?.message,
      raw: data
    };
  }

  if (discountPercentRaw) {
    return {
      code: normalizedCode,
      type: (isTypeDiscount ? 'DISCOUNT_PERCENT' : (typeFromApi ?? 'DISCOUNT_PERCENT')) as CouponKind,
      discountPercent: Math.min(100, discountPercentRaw),
      message: data?.message ?? data?.coupon?.message,
      raw: data
    };
  }

  if (isTypeFreeDays && !freeDays) {
    throw new Error('Cupom de dias grátis inválido');
  }

  throw new Error(data?.message || data?.error || 'Cupom inválido');
};

export const isFreeDaysCoupon = (coupon: AppliedCoupon | null | undefined): boolean =>
  (coupon?.type === 'FREE_DAYS') || (Number(coupon?.freeDays) > 0);

export const getCouponDiscountPercent = (coupon: AppliedCoupon | null | undefined): number =>
  coupon?.type === 'FREE_DAYS' ? 0 : Math.max(0, Number(coupon?.discountPercent) || 0);

export const getCouponFreeDays = (coupon: AppliedCoupon | null | undefined): number =>
  Math.max(0, Number(coupon?.freeDays) || 0);

export const getCouponSummaryText = (coupon: AppliedCoupon): string => {
  if (isFreeDaysCoupon(coupon)) {
    const days = getCouponFreeDays(coupon);
    return `Cupom ${coupon.code} aplicado: ${days} dias grátis.`;
  }

  const percent = getCouponDiscountPercent(coupon);
  return `Cupom ${coupon.code} aplicado: ${percent}% de desconto.`;
};
